import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { site } from "@/data/site";

const FAQS = [
  {
    q: "What app is the training delivered through?",
    a: "Everything runs through Trainerize. Your full program lives there — exercises with demo videos, sets and reps, progress tracking, and direct messaging — so your training, your data, and our conversations stay in one place on your phone.",
  },
  {
    q: "How much time do I need each week?",
    a: "Most drivers train 3 to 5 sessions per week, roughly 45 to 75 minutes each. The program flexes around your race weeks, travel, work, and school — heavy blocks when you're home, maintenance work when you're at the track.",
  },
  {
    q: "Which tier is right for me?",
    a: "Tier 1 fits kart and club drivers who want structure without high-touch coaching. Tier 2 is where most drivers land — you're actively racing and want testing, data, and a program built around your calendar. Tier 3 is for packed seasons and pro-level targets where you want me heavily involved week to week. Not sure? DM me and we'll figure it out together.",
  },
  {
    q: "Do I need a full gym?",
    a: "No. The program is built around whatever equipment you actually have access to — full gym, home setup, or hotel gyms during race travel. When your access changes, the plan adjusts with it.",
  },
  {
    q: "I'm a sim racer / karting parent — is this for me?",
    a: "Yes. I work with karters, sim racers, rally drivers, and circuit racers. The cockpit demands are the same: neck and core endurance, reaction speed, heat tolerance, and focus under fatigue. For younger drivers, parents stay involved in the process.",
  },
  {
    q: "How do I get started?",
    a: "Send me a DM on Instagram or an email. We'll talk about what you're racing, your schedule, and where you want to be. From there I onboard you onto Trainerize and your first block starts.",
  },
];

function FaqItem({ item, index, open, onToggle }) {
  return (
    <Reveal delay={index * 0.06}>
      <div className="border-2 border-white/12 bg-surface" data-testid={`faq-item-${index}`}>
        <button
          onClick={onToggle}
          className="flex w-full items-center gap-5 p-6 text-left lg:p-8"
          data-testid={`faq-toggle-${index}`}
          aria-expanded={open}
        >
          <span className={`font-display text-sm font-black ${open ? "text-purple" : "text-gold"}`}>
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="flex-1 font-display text-base font-black uppercase tracking-tight text-white sm:text-lg">
            {item.q}
          </span>
          <motion.span
            animate={{ rotate: open ? 45 : 0 }}
            transition={{ duration: 0.25 }}
            className={`shrink-0 ${open ? "text-purple" : "text-white/50"}`}
          >
            <Plus className="h-5 w-5" strokeWidth={3} />
          </motion.span>
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <p
                className="border-t border-white/10 px-6 pb-7 pt-5 text-sm font-medium leading-relaxed text-white/70 lg:px-8 lg:pl-[4.5rem]"
                data-testid={`faq-answer-${index}`}
              >
                {item.a}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
}

export default function Faq() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" className="relative py-28 lg:py-40" data-testid="faq-section">
      <div className="halftone pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">
                <span className="h-px w-10 bg-gold" />
                FAQ
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl">
                Before you <span className="text-purple">send it.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-sm text-base font-medium leading-relaxed text-white/65">
                The questions every driver asks first. Anything else — DM{" "}
                <a
                  href={site.igUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-gold underline-offset-4 hover:underline"
                  data-testid="faq-ig-link"
                >
                  {site.igHandle}
                </a>{" "}
                and I'll answer directly.
              </p>
            </Reveal>
          </div>

          <div className="flex flex-col gap-4 lg:col-span-8">
            {FAQS.map((item, i) => (
              <FaqItem
                key={i}
                item={item}
                index={i}
                open={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
